import { useCallback, useContext, useEffect, useState } from 'react';
import { AccessPermissionContext } from './access-permission.hook';
import { useLocation } from 'react-router-dom';
import { getKeysByValue } from '@/utils/common';
import { PERMISSION_BY_KEY } from '@/constants/permissions.constant';
import { PermissionField } from '@/enums/permission.enum';

export default function usePermission() {
  const permissionContext = useContext(AccessPermissionContext);
  const { pathname } = useLocation();

  const [permission, setPermission] = useState({
    [PermissionField.VIEW]: false,
    [PermissionField.CREATE]: false,
    [PermissionField.UPDATE]: false,
    [PermissionField.DELETE]: false,
    [PermissionField.IMPORT_FILE]: false,
    [PermissionField.EXPORT_FILE]: false
  });

  const getPermissions = (values: number[]) => {
    const newPermissions = permission;
    values.forEach((value) => {
      if (Object.values(PermissionField).includes(value)) {
        newPermissions[value as PermissionField] = true;
      }
      newPermissions[PermissionField.VIEW] = true;
    });

    return newPermissions;
  };

  const getAllowedPermissions = useCallback(
    (indexes?: string[]) => {
      const roleIndexes = indexes ?? permissionContext?.roleIndexes ?? [];
      const roleIndexesByPath = getKeysByValue(PERMISSION_BY_KEY, pathname);

      const roleIndexesWithPermission = roleIndexesByPath.filter((item) =>
        roleIndexes.includes(item)
      );

      return roleIndexesWithPermission.map((item) =>
        Number(item.split('-').pop())
      );
    },
    [pathname, permissionContext?.roleIndexes]
  );

  const updatePermissions = (values: number[]) => {
    setPermission((prev) => {
      const newPermissions = { ...prev };

      values.forEach((value) => {
        if (Object.values(PermissionField).includes(value)) {
          newPermissions[value as PermissionField] = true;
        }
        newPermissions[PermissionField.VIEW] = true;
      });

      return newPermissions;
    });
  };

  useEffect(() => {
    const allowedPermissions = getAllowedPermissions();
    updatePermissions(allowedPermissions);
  }, [getAllowedPermissions]);

  return {
    viewable: permission[PermissionField.VIEW],
    creatable: permission[PermissionField.CREATE],
    updatable: permission[PermissionField.UPDATE],
    deletable: permission[PermissionField.DELETE],
    importable: permission[PermissionField.IMPORT_FILE],
    exportable: permission[PermissionField.EXPORT_FILE],
    getPermissions,
    getAllowedPermissions
  };
}
